﻿export function initHtmlToTextPage() {
    const root = document.querySelector("[data-html-to-text-page]");
    if (!root) return;

    const page = {
        input: root.querySelector("[data-html-text-input]"),
        output: root.querySelector("[data-html-text-output]"),

        preserveLinks: root.querySelector("[data-html-text-preserve-links]"),
        formatLists: root.querySelector("[data-html-text-format-lists]"),
        keepHeadings: root.querySelector("[data-html-text-keep-headings]"),
        removeNoise: root.querySelector("[data-html-text-remove-noise]"),
        normalize: root.querySelector("[data-html-text-normalize]"),
        lineBreak: root.querySelector("[data-html-text-line-break]"),

        convert: root.querySelector("[data-html-text-convert]"),
        copy: root.querySelector("[data-html-text-copy]"),
        download: root.querySelector("[data-html-text-download]"),
        sample: root.querySelector("[data-html-text-sample]"),

        metaInput: root.querySelector("[data-html-text-meta-input]"),
        metaOutput: root.querySelector("[data-html-text-meta-output]"),
        metaWords: root.querySelector("[data-html-text-meta-words]"),
        metaLinks: root.querySelector("[data-html-text-meta-links]")
    };

    const liveInputs = [
        page.input,
        page.preserveLinks,
        page.formatLists,
        page.keepHeadings,
        page.removeNoise,
        page.normalize,
        page.lineBreak
    ];

    liveInputs.filter(Boolean).forEach(input => {
        input.addEventListener("input", () => convertHtmlToText(page));
        input.addEventListener("change", () => convertHtmlToText(page));
    });

    page.convert?.addEventListener("click", () => {
        convertHtmlToText(page, true);
    });

    page.copy?.addEventListener("click", async () => {
        await copyOutput(page);
    });

    page.download?.addEventListener("click", () => {
        downloadText(page);
    });

    page.sample?.addEventListener("click", () => {
        if (!page.input) return;

        page.input.value = getSampleHtml();
        convertHtmlToText(page, true);
    });

    convertHtmlToText(page);
}

function convertHtmlToText(page, animate = false) {
    if (!page.input || !page.output) return;

    const html = page.input.value || "";
    const options = {
        preserveLinks: Boolean(page.preserveLinks?.checked),
        formatLists: Boolean(page.formatLists?.checked),
        keepHeadings: Boolean(page.keepHeadings?.checked),
        removeNoise: Boolean(page.removeNoise?.checked),
        normalize: Boolean(page.normalize?.checked),
        lineBreak: page.lineBreak?.value || "normal"
    };

    const result = htmlToText(html, options);

    page.output.value = result.text;

    if (animate) {
        page.output.classList.remove("is-generated");
        void page.output.offsetWidth;
        page.output.classList.add("is-generated");
    }

    updateMeta(page, html, result);
}

function htmlToText(html, options) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(String(html || ""), "text/html");

    if (options.removeNoise) {
        doc.querySelectorAll("script, style, noscript, template, svg, canvas, iframe").forEach(x => x.remove());
    }

    const links = Array.from(doc.querySelectorAll("a[href]"));

    if (options.preserveLinks) {
        links.forEach(link => {
            const href = link.getAttribute("href")?.trim();
            const text = normalizeInlineText(link.textContent);

            if (!href) return;

            link.replaceWith(doc.createTextNode(`${text} (${href})`));
        });
    }

    if (options.formatLists) {
        doc.querySelectorAll("li").forEach(li => {
            li.insertBefore(doc.createTextNode("- "), li.firstChild);
            li.appendChild(doc.createTextNode("\n"));
        });
    }

    if (options.keepHeadings) {
        doc.querySelectorAll("h1,h2,h3,h4,h5,h6").forEach(heading => {
            heading.insertBefore(doc.createTextNode("\n\n"), heading.firstChild);
            heading.appendChild(doc.createTextNode("\n"));
        });
    }

    doc.querySelectorAll("br").forEach(br => {
        br.replaceWith(doc.createTextNode("\n"));
    });

    doc.querySelectorAll("p,div,section,article,header,footer,main,aside,nav,blockquote,pre,tr").forEach(block => {
        block.appendChild(doc.createTextNode("\n"));
    });

    doc.querySelectorAll("td,th").forEach(cell => {
        cell.appendChild(doc.createTextNode("\t"));
    });

    let text = doc.body.textContent || "";

    text = text
        .replace(/\u00A0/g, " ")
        .replace(/[ \t]+\n/g, "\n")
        .replace(/\n[ \t]+/g, "\n");

    if (options.normalize) {
        text = normalizeText(text, options.lineBreak);
    }

    return {
        text,
        links: links.length,
        words: countWords(text)
    };
}

function normalizeText(text, lineBreakStyle) {
    let value = String(text || "")
        .replace(/[ \t]{2,}/g, " ")
        .replace(/\n{3,}/g, "\n\n")
        .trim();

    if (lineBreakStyle === "compact") {
        value = value
            .replace(/\n{2,}/g, "\n")
            .replace(/[ \t]{2,}/g, " ")
            .trim();
    }

    if (lineBreakStyle === "single-line") {
        value = value
            .replace(/\s+/g, " ")
            .trim();
    }

    return value;
}

function normalizeInlineText(value) {
    return String(value || "")
        .replace(/\s+/g, " ")
        .trim();
}

function countWords(value) {
    const words = String(value || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);

    return words.length;
}

function updateMeta(page, input, result) {
    if (page.metaInput) page.metaInput.textContent = formatNumber(input.length);
    if (page.metaOutput) page.metaOutput.textContent = formatNumber(result.text.length);
    if (page.metaWords) page.metaWords.textContent = formatNumber(result.words);
    if (page.metaLinks) page.metaLinks.textContent = formatNumber(result.links);
}

async function copyOutput(page) {
    const value = page.output?.value || "";

    if (!value) return;

    try {
        await navigator.clipboard.writeText(value);
        showDone(page.copy, "Copied");
    } catch {
        fallbackCopy(value);
        showDone(page.copy, "Copied");
    }
}

function downloadText(page) {
    const value = page.output?.value || "";

    if (!value) return;

    const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-");

    const blob = new Blob([value], {
        type: "text/plain;charset=utf-8"
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `html-to-text-${timestamp}.txt`;

    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(url);
}

function fallbackCopy(value) {
    const textarea = document.createElement("textarea");
    textarea.value = value;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    textarea.style.pointerEvents = "none";

    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    textarea.remove();
}

function showDone(button, text) {
    if (!button) return;

    const old = button.innerHTML;

    button.classList.add("is-done");
    button.innerHTML = `<i class="fa-regular fa-check"></i><span>${text}</span>`;

    window.setTimeout(() => {
        button.innerHTML = old;
        button.classList.remove("is-done");
    }, 950);
}

function formatNumber(value) {
    return new Intl.NumberFormat().format(value || 0);
}

function getSampleHtml() {
    return `<article class="post">
    <h1>HTML to Text Example</h1>
    <p>This sample contains <strong>formatted text</strong>, a <a href="https://tools.multifonks.com">website link</a> and a list.</p>

    <h2>Use cases</h2>
    <ul>
        <li>Clean copied HTML</li>
        <li>Prepare plain text emails</li>
        <li>Extract readable content</li>
    </ul>

    <p>Convert this markup into clean plain text output.</p>
</article>`;
}